import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Snackbar,
  SnackbarCloseReason,
  TextField,
  Typography,
} from '@mui/material';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import * as yup from 'yup';
import { useAuthContext } from '../../contexts';
import { ConstellationBackground } from './ConstellationBackground';

const loginSchema = yup.object().shape({
  usuario: yup.string().required('Usuário é obrigatório'),
  senha: yup.string().required('Senha é obrigatória').min(5, 'Mínimo 5 caracteres'),
});

const REMEMBER_ME_STORAGE_KEY = 'APP_REMEMBERED_USER';

// Paleta da tela de login — instrucoes_login_siac_css.txt ("TELA DE LOGIN
// SIAC 100% CSS"). Visual único, não acompanha o tema do PDV.
const SIAC = {
  dark: '#001E27',
  cyan: '#00E2F4',
  cyanHover: '#00CDDE',
  cyanIcon: '#00CFE0',
  bgLight: '#F7FAFC',
  text: '#001E27',
  muted: '#687E8A',
  labelText: '#314954',
  border: '#D9E4E8',
};

// Breakpoints próprios do spec (1100/850/480) — não batem com os do MUI
// (900/1200/...), então usados como media queries literais, na frente dos
// valores "base" (sempre aplicados) pra sobrescrever só abaixo de cada
// limite. Ordem no objeto importa: os mais estreitos vêm por último, pra
// ganhar quando mais de uma faixa bate ao mesmo tempo.
const MQ_1100 = '@media (max-width:1100px)';
const MQ_850 = '@media (max-width:850px)';
const MQ_480 = '@media (max-width:480px)';

// Campos (Usuário/Senha) — ícone dentro do próprio campo (cinza-ciano
// discreto), sem bloco colorido lateral. Altura/raio/sombra batem com o
// spec (--siac-radius-input:13px, --siac-shadow-input).
const fieldSx = {
  '& .MuiOutlinedInput-root': {
    height: 58,
    borderRadius: '13px',
    backgroundColor: 'rgba(255, 255, 255, 0.92)',
    fontSize: '15px',
    color: SIAC.text,
    boxShadow: '0 2px 5px rgba(0, 30, 39, 0.035), 0 10px 22px rgba(0, 30, 39, 0.035)',
    transition: 'border-color 180ms ease, box-shadow 180ms ease, background 180ms ease',
    '& fieldset': { borderColor: SIAC.border },
    '&:hover fieldset': { borderColor: '#C7D8DE' },
    '&.Mui-focused': { backgroundColor: '#FFFFFF' },
    '&.Mui-focused fieldset': { borderColor: SIAC.cyan, borderWidth: '1.5px' },
    [MQ_480]: { height: 55 },
  },
  '& .MuiFormHelperText-root': { fontSize: '0.78rem', mx: 0.25 },
};

export const Login: React.FC = () => {
  const { login } = useAuthContext();

  const [isLoading, setIsLoading] = useState(false);
  const [senhaError, setSenhaError] = useState('');
  const [usuarioError, setUsuarioError] = useState('');
  const [senha, setSenha] = useState('');
  const [usuario, setUsuario] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [openmsg, setOpenmsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [tms, setTms] = useState<'warning' | 'error' | 'info' | 'success'>('warning');
  const senhaInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const rememberedUser = localStorage.getItem(REMEMBER_ME_STORAGE_KEY);
    if (rememberedUser) {
      setUsuario(rememberedUser);
      setRememberMe(true);
    }
  }, []);

  const handleCloseMsg = (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') return;
    setOpenmsg(false);
  };

  const handleSubmit = () => {
    setIsLoading(true);
    setSenhaError('');
    setUsuarioError('');

    loginSchema
      .validate({ usuario: usuario.trim(), senha: senha.trim() }, { abortEarly: false })
      .then(dadosValidados => {
        login(dadosValidados.usuario, dadosValidados.senha)
          .then(() => {
            if (rememberMe) {
              localStorage.setItem(REMEMBER_ME_STORAGE_KEY, dadosValidados.usuario);
            } else {
              localStorage.removeItem(REMEMBER_ME_STORAGE_KEY);
            }
            setIsLoading(false);
          })
          .catch((error) => {
            setIsLoading(false);
            setTms('error');
            setMsg(error?.message || 'Usuário ou senha incorretos');
            setOpenmsg(true);
          });
      })
      .catch((errors: yup.ValidationError) => {
        setIsLoading(false);
        errors.inner.forEach(error => {
          if (error.path === 'usuario') setUsuarioError(error.message);
          if (error.path === 'senha') setSenhaError(error.message);
        });
      });
  };

  return (
    <Box
      sx={{
        width: '100vw',
        // height (não minHeight) — trava a página em 100dvh de verdade. Com
        // só minHeight, se o conteúdo de um dos painéis passasse da altura
        // da tela (telas baixas), a LINHA do grid crescia com ele e a
        // página toda ficava mais alta que a viewport, aparecendo scroll.
        // Os espaçamentos verticais dos painéis usam clamp(...,vh,...) —
        // ver abaixo — pra encolher em vez de estourar essa altura fixa.
        height: '100dvh',
        overflow: 'hidden',
        display: 'grid',
        // 38%/62% em telas normais, 42%/58% até 1100px, uma coluna só
        // (painel escuro escondido) até 850px — ver seção 1/16 do spec.
        // Media query como chave IRMÃ no nível de cima do sx (não dentro do
        // valor de gridTemplateColumns) — misturar 'xs' (chave que o MUI
        // reconhece) com uma media query crua ([MQ_1100]/[MQ_850]) dentro do
        // valor de uma propriedade não funciona no sistema de breakpoints do
        // MUI: ele só entende xs/sm/md/lg/xl nesse formato e ignora as
        // chaves customizadas em silêncio, deixando o valor de 'xs' sempre
        // valendo (era exatamente por isso que a tela nunca saía do layout
        // de desktop no celular).
        gridTemplateColumns: '38% 62%',
        [MQ_1100]: { gridTemplateColumns: '42% 58%' },
        [MQ_850]: { gridTemplateColumns: '1fr' },
        position: 'relative',
        bgcolor: SIAC.bgLight,
      }}
    >
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={openmsg}
        autoHideDuration={tms === 'error' ? null : 3000}
        onClose={handleCloseMsg}
      >
        <Alert
          onClose={handleCloseMsg}
          severity={tms}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {msg}
        </Alert>
      </Snackbar>

      {/* PAINEL ESQUERDO — escondido abaixo de 850px (spec seção 16). Fundo
          100% CSS (gradiente + grade de pontos + formas, ver
          TechDecoration/LoginTechDecoration.tsx), sem nenhuma imagem. */}
      <Box
        sx={{
          display: 'block',
          [MQ_850]: { display: 'none' },
          position: 'relative',
          // Altura exata da linha do grid (já travada em 100dvh lá em cima)
          // — overflowX escondido segura o sangramento das formas
          // decorativas pras bordas; overflowY:auto é só uma rede de
          // segurança (não deveria disparar, já que o padding/conteúdo
          // aqui é enxuto o bastante pra caber em qualquer altura comum).
          height: '100%',
          overflowX: 'hidden',
          overflowY: 'auto',
          color: '#FFFFFF',
          p: 'clamp(24px, 6vh, 54px) clamp(28px, 4vw, 48px) clamp(20px, 5vh, 42px)',
          [MQ_1100]: { p: 'clamp(20px, 5vh, 42px) 34px' },
          background: `
            radial-gradient(circle at 35% 45%, rgba(0, 226, 244, 0.07) 0, rgba(0, 226, 244, 0.025) 30%, transparent 58%),
            linear-gradient(145deg, #003A50 0%, #002C3D 38%, #001E27 72%, #001720 100%)
          `,
        }}
      >
        <ConstellationBackground />

        {/* Conteúdo real — acima da decoração (zIndex:2). */}
        <Box
          sx={{
            position: 'relative',
            zIndex: 2,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* 1. Logo — largura em vez de altura (spec: width:150px,
              max-width:40%), preserva a proporção original via height:auto. */}
          <Box
            component="img"
            src="/img/siac-logo-full.png"
            alt="SIAC"
            sx={{ width: 'clamp(120px, 13vw, 150px)', maxWidth: '40%', height: 'auto', filter: 'brightness(0) invert(1)' }}
          />

          {/* 2. ERP TORQ */}
          <Typography
            sx={{
              mt: '16px',
              fontSize: '11px',
              letterSpacing: '0.22em',
              fontWeight: 600,
              color: 'rgba(255, 255, 255, 0.55)',
            }}
          >
            ERP TORQ
          </Typography>

          {/* 3. Bloco de boas-vindas — centralizado verticalmente no espaço
              sobrando (margin auto topo/baixo, como o spec pede). */}
          <Box sx={{ mt: 'auto', mb: 'auto', maxWidth: 370 }}>
            <Typography
              sx={{
                m: 0,
                fontSize: 'clamp(30px, 2.3vw, 42px)',
                lineHeight: 1.08,
                fontWeight: 750,
                letterSpacing: '-0.04em',
                // Sem isso, herdava o body1.color escuro do tema (Light.ts)
                // em vez do branco do painel — mesma cor da logo.
                color: '#FFFFFF',
              }}
            >
              Bem-vindo de{' '}
              <Box component="span" sx={{ color: SIAC.cyan }}>
                volta!
              </Box>
            </Typography>
            <Typography
              sx={{
                mt: '16px',
                fontSize: '15px',
                lineHeight: 1.65,
                color: 'rgba(255, 255, 255, 0.66)',
              }}
            >
              Acesse sua conta para continuar utilizando o sistema.
            </Typography>
            {/* Traço ciano — só decorativo (spec seção 5). */}
            <Box sx={{ width: 78, height: 3, mt: '22px', borderRadius: 999, bgcolor: SIAC.cyan }} />
          </Box>
        </Box>
      </Box>

      {/* LADO DIREITO CLARO — fundo + formas 100% CSS (spec seções 6/7). */}
      <Box
        sx={{
          position: 'relative',
          // Mesma lógica do painel esquerdo — altura exata da linha do
          // grid, X escondido (pras formas decorativas que sangram pra
          // fora), Y com scroll só como rede de segurança.
          height: '100%',
          overflowX: 'hidden',
          overflowY: 'auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 'clamp(20px, 5vh, 48px) 64px',
          [MQ_850]: { p: '32px 24px' },
          [MQ_480]: { p: '26px 20px' },
          background: `
            radial-gradient(circle at 76% 50%, rgba(0, 226, 244, 0.055), transparent 34%),
            linear-gradient(135deg, #FFFFFF 0%, #FAFCFD 55%, #F1F7F9 100%)
          `,
          '&::before': {
            content: '""',
            position: 'absolute',
            width: 'clamp(480px, 37.5vw, 900px)',
            height: 'clamp(480px, 37.5vw, 900px)',
            right: 'clamp(-320px, -13vw, -180px)',
            top: 'clamp(-190px, -6.8vw, -90px)',
            transform: 'rotate(45deg)',
            border: 'clamp(30px, 2.8vw, 54px) solid rgba(10, 90, 116, 0.028)',
            borderRadius: 'clamp(50px, 4.7vw, 90px)',
            pointerEvents: 'none',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            width: 'clamp(360px, 28vw, 675px)',
            height: 'clamp(360px, 28vw, 675px)',
            right: 'clamp(-300px, -12vw, -170px)',
            top: 'clamp(30px, 4.2vw, 100px)',
            transform: 'rotate(45deg)',
            border: 'clamp(28px, 2.6vw, 50px) solid rgba(0, 226, 244, 0.035)',
            borderRadius: 'clamp(40px, 4vw, 78px)',
            pointerEvents: 'none',
          },
        }}
      >
        {/* Terceira faixa opcional (spec seção 7) — precisa ser um elemento
            de verdade (só dá pra ter ::before/::after num Box só). */}
        <Box
          sx={{
            position: 'absolute',
            width: 'clamp(300px, 23.4vw, 450px)',
            height: 'clamp(300px, 23.4vw, 450px)',
            right: '10%',
            top: '16%',
            borderRadius: 'clamp(34px, 3.75vw, 72px)',
            transform: 'rotate(45deg)',
            border: 'clamp(22px, 2.2vw, 42px) solid rgba(0, 30, 39, 0.018)',
            pointerEvents: 'none',
          }}
        />

        <Box
          sx={{
            position: 'relative',
            zIndex: 2,
            width: '100%',
            maxWidth: 430,
            [MQ_850]: { maxWidth: 420 },
          }}
        >
          <Typography
            sx={{
              m: 0,
              color: SIAC.text,
              fontSize: 'clamp(27px, 2.2vw, 31px)',
              [MQ_480]: { fontSize: '27px' },
              lineHeight: 1.15,
              fontWeight: 750,
              letterSpacing: '-0.035em',
            }}
          >
            Acesse sua conta
          </Typography>
          <Typography sx={{ mt: '8px', mb: 'clamp(14px, 2.5vh, 26px)', color: SIAC.muted, fontSize: '15px' }}>
            Informe suas credenciais para entrar
          </Typography>

          <Box sx={{ mb: 'clamp(12px, 2vh, 22px)' }}>
            <Typography component="label" sx={{ display: 'block', mb: '8px', color: SIAC.labelText, fontSize: '12px', fontWeight: 650 }}>
              Usuário
            </Typography>
            <TextField
              fullWidth
              autoFocus
              variant="outlined"
              placeholder="Usuário"
              hiddenLabel
              value={usuario}
              disabled={isLoading}
              error={!!usuarioError}
              helperText={usuarioError}
              onKeyDown={e => {
                setUsuarioError('');
                if (e.key === 'Enter') {
                  e.preventDefault();
                  senhaInputRef.current?.focus();
                }
              }}
              onChange={e => setUsuario(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonOutlineRoundedIcon sx={{ color: SIAC.cyanIcon, fontSize: 22 }} />
                  </InputAdornment>
                ),
              }}
              sx={fieldSx}
            />
          </Box>

          <Box sx={{ mb: 'clamp(12px, 2vh, 22px)' }}>
            <Typography component="label" sx={{ display: 'block', mb: '8px', color: SIAC.labelText, fontSize: '12px', fontWeight: 650 }}>
              Senha
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Senha"
              hiddenLabel
              type={mostrarSenha ? 'text' : 'password'}
              value={senha}
              disabled={isLoading}
              error={!!senhaError}
              helperText={senhaError}
              inputRef={senhaInputRef}
              onKeyDown={e => {
                setSenhaError('');
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
              onChange={e => setSenha(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon sx={{ color: SIAC.cyanIcon, fontSize: 21 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setMostrarSenha(old => !old)}
                      edge="end"
                      tabIndex={-1}
                      sx={{ color: SIAC.muted }}
                    >
                      {mostrarSenha ? <VisibilityOffRoundedIcon fontSize="small" /> : <VisibilityRoundedIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={fieldSx}
            />
          </Box>

          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={rememberMe}
                disabled={isLoading}
                onChange={e => setRememberMe(e.target.checked)}
                sx={{
                  color: SIAC.border,
                  '& svg': { fontSize: 18 },
                  '&.Mui-checked': { color: SIAC.cyan },
                }}
              />
            }
            label={
              <Typography sx={{ fontSize: '13px', color: SIAC.text }}>
                Lembrar-me
              </Typography>
            }
            sx={{ ml: -0.5, mt: '3px', mb: 'clamp(14px, 2.2vh, 25px)' }}
          />

          <Button
            fullWidth
            variant="contained"
            disabled={isLoading}
            onClick={handleSubmit}
            endIcon={isLoading ? <CircularProgress size={18} color="inherit" /> : <ArrowForwardRoundedIcon />}
            sx={{
              height: 54,
              borderRadius: '12px',
              fontWeight: 700,
              fontSize: '15px',
              textTransform: 'none',
              background: 'linear-gradient(90deg, #00D9EB 0%, #00E2F4 55%, #11D7E7 100%)',
              color: SIAC.dark,
              boxShadow: '0 8px 24px rgba(0, 226, 244, 0.20)',
              transition: 'transform 150ms ease, box-shadow 150ms ease, filter 150ms ease',
              '&:hover': {
                transform: 'translateY(-1px)',
                filter: 'brightness(.98)',
                boxShadow: '0 11px 26px rgba(0, 226, 244, 0.25)',
                background: 'linear-gradient(90deg, #00D9EB 0%, #00E2F4 55%, #11D7E7 100%)',
              },
              '&:active': { transform: 'translateY(0)' },
              [MQ_480]: { height: 52 },
            }}
          >
            Entrar
          </Button>

          {/* Informação de segurança — spec seção 13. */}
          <Box
            sx={{
              mt: 'clamp(20px, 6vh, 90px)',
              [MQ_850]: { mt: 'clamp(16px, 4vh, 60px)' },
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px',
              maxWidth: 360,
              color: SIAC.muted,
              fontSize: '12px',
              lineHeight: 1.5,
            }}
          >
            <VerifiedUserOutlinedIcon sx={{ fontSize: 18, color: SIAC.cyanIcon, flexShrink: 0, mt: '1px' }} />
            <Typography sx={{ fontSize: 'inherit', color: 'inherit', lineHeight: 'inherit' }}>
              Seus dados estão protegidos com segurança de nível empresarial.
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
